import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

async function copyTextToClipboard(value: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = value;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

function shortenId(id: string, visibleChars: number): string {
  const trimmed = id.trim();
  return trimmed.length > visibleChars ? `${trimmed.slice(0, visibleChars)}…` : trimmed;
}

export interface CopyableIdProps extends Omit<
  React.ComponentProps<'button'>,
  'children' | 'onClick' | 'value'
> {
  /** Full id — copied to the clipboard on click and shown in the tooltip. */
  id: string;
  /** Leading chars shown when `shorten` is on. Defaults to 8. */
  visibleChars?: number;
  /** Show `8…`-style prefix instead of the whole id. Defaults to true. */
  shorten?: boolean;
  /** Tooltip confirmation after a successful copy. Defaults to `Copied`. */
  copiedLabel?: string;
}

/**
 * Every guid/id display across the app goes through here: shortened text
 * with the full id in a tooltip, a copy icon + surface highlight on hover,
 * and click-to-copy with a `Copied` confirmation.
 */
function CopyableId({
  id,
  visibleChars = 8,
  shorten = true,
  copiedLabel = 'Copied',
  className,
  ...props
}: CopyableIdProps) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);
  React.useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  if (id.trim() === '') {
    return <span className={cn('tabular-nums', className)}>—</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`Copy ${id}`}
          onClick={(event) => {
            event.stopPropagation();
            void copyTextToClipboard(id).then((ok) => {
              if (!ok) return;
              setCopied(true);
              if (timer.current !== null) window.clearTimeout(timer.current);
              timer.current = window.setTimeout(() => setCopied(false), 1500);
            });
          }}
          className={cn(
            'group inline-flex max-w-full min-w-0 cursor-pointer items-center gap-1 rounded tabular-nums transition-colors hover:bg-muted hover:text-foreground',
            className,
          )}
          {...props}
        >
          <span className="truncate">{shorten ? shortenId(id, visibleChars) : id}</span>
          {copied ? (
            <Check aria-hidden className="size-3.5 shrink-0" />
          ) : (
            <Copy
              aria-hidden
              className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
            />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs break-all">
        {copied ? copiedLabel : id}
      </TooltipContent>
    </Tooltip>
  );
}

export { CopyableId, copyTextToClipboard };
